// @ts-check

/**
 * Packaging Options — mirrors one buy-box upgrade tick box as a radio choice
 * (standard packaging vs upgrade) inside the Packaging & Gift Options tab.
 *
 * This component never adds anything to the cart: the upgrade-product-component
 * in the buy box owns the add. The two stay in sync via the shared
 * UpgradeEvents, matched by the upgrade's variant ID — the tab lives in a
 * different section from the buy box, so section-scoped matching can't be used
 * here.
 *
 * Which upgrade appears is decided in Liquid by this block's own metafield
 * setting (see the block's comment for why it can't read the upgrade block's).
 * If no upgrade block on the page owns that same product, nothing would ever
 * add it, so the upgrade card is hidden and the tab falls back to showing
 * standard packaging alone.
 *
 * The gift bag add-to-cart in the same block is a plain native product form
 * (product-form-component) and needs no JS from this file.
 */

import { Component } from '@theme/component';
import { UpgradeEvents } from '@theme/upgrade-product';

/**
 * @typedef {object} PackagingOptionsRefs
 * @property {HTMLInputElement[]} options
 * @property {HTMLScriptElement} payload
 * @property {HTMLElement} [upgradeOption]
 *
 * @extends Component<PackagingOptionsRefs>
 */
class PackagingOptionsComponent extends Component {
  requiredRefs = [];
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    if (!this.refs.payload || !this.refs.options?.length) return;

    this.data = JSON.parse(this.refs.payload.textContent || '{}');

    const { signal } = this.#abortController;

    for (const option of this.refs.options) {
      option.addEventListener('change', this.#onOptionChange, { signal });
    }

    // One listener serves both events: `change` announcements from the owning
    // upgrade block and `set` requests from other mirrors. Both carry
    // upgradeVariantId.
    document.addEventListener(UpgradeEvents.change, this.#onExternalUpdate, { signal });
    document.addEventListener(UpgradeEvents.set, this.#onExternalUpdate, { signal });

    // The buy box may be parsed after this block, so wait for a complete
    // document before deciding whether an owner exists.
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', this.#seedFromOwner, { once: true, signal });
    } else {
      this.#seedFromOwner();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  get #upgradeSelected() {
    return this.refs.options?.find((option) => option.checked)?.value === 'upgrade';
  }

  /** @param {boolean} upgraded */
  #setSelection(upgraded) {
    const value = upgraded ? 'upgrade' : 'standard';
    for (const option of this.refs.options) {
      option.checked = option.value === value;
    }
  }

  /**
   * The buy-box tick box for the same upgrade product, if present. Matched on
   * the server-rendered payload rather than a property, so it works however
   * the two components' definitions happen to be ordered.
   */
  get #ownerCheckbox() {
    for (const component of document.querySelectorAll('upgrade-product-component')) {
      const payload = component.querySelector('script[ref="payload"]');
      if (!payload) continue;
      try {
        const data = JSON.parse(payload.textContent || '{}');
        if (String(data.upgradeVariantId) === String(this.data?.upgradeVariantId)) {
          return /** @type {HTMLInputElement | null} */ (
            component.querySelector('input[ref="checkbox"]')
          );
        }
      } catch {
        // Malformed payload — skip this component.
      }
    }
    return null;
  }

  #seedFromOwner = () => {
    const checkbox = this.#ownerCheckbox;

    if (!checkbox) {
      // Nothing on the page would add this upgrade to the cart, so don't
      // offer it. The CSS drops the remaining radio to a static card.
      if (this.refs.upgradeOption) this.refs.upgradeOption.hidden = true;
      this.dataset.upgradeUnavailable = 'true';
      return;
    }

    delete this.dataset.upgradeUnavailable;
    this.#setSelection(checkbox.checked);
  };

  #onOptionChange = () => {
    this.dispatchEvent(
      new CustomEvent(UpgradeEvents.set, {
        bubbles: true,
        detail: {
          checked: this.#upgradeSelected,
          upgradeVariantId: this.data?.upgradeVariantId,
        },
      })
    );
  };

  /** @param {CustomEvent} event */
  #onExternalUpdate = (event) => {
    if (event.target === this) return;
    if (event.detail?.upgradeVariantId == null) return;
    if (String(event.detail.upgradeVariantId) !== String(this.data?.upgradeVariantId)) return;
    this.#setSelection(Boolean(event.detail.checked));
  };
}

if (!customElements.get('packaging-options-component')) {
  customElements.define('packaging-options-component', PackagingOptionsComponent);
}
